import { createRouter, RouterProvider } from "@tanstack/react-router";
import ReactDOM from "react-dom/client";
import { queryClient } from "./queryClient";
import { routeTree } from "./routeTree.gen";
import { getAppScrollRestorationKey } from "./utils/scrollRestoration";

const router = createRouter({
  routeTree,
  context: {
    queryClient,
    getTitle: undefined,
  },
  defaultPreload: "intent",
  scrollRestoration: true,
  getScrollRestorationKey: getAppScrollRestorationKey,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

import "@fontsource/lato/700.css";
import "@fontsource/lato/900.css";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";

import * as Sentry from "@sentry/react";

import type { PropsWithChildren } from "react";

Sentry.init({
  dsn: import.meta.env.WAI_SENTRY_DSN,
  environment: import.meta.env.WAI_MODE,
  sendDefaultPii: true,
  tracesSampleRate: 1,
  profilesSampleRate: import.meta.env.DEV ? 1 : 0.2,
  tunnel: `${import.meta.env.WAI_API_URL}/tunnel`,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.tanstackRouterBrowserTracingIntegration(router),
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1,
  tracePropagationTargets: [import.meta.env.WAI_API_URL, new URL(OIDC_METADATA_URL).origin],
  propagateTraceparent: true,
});

import { CssBaseline } from "@mui/material";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import { broadcastQueryClient } from "@tanstack/query-broadcast-client-experimental";
import { QueryClientProvider } from "@tanstack/react-query";
import { type ReactNode, StrictMode } from "react";
import { AuthProvider } from "react-oidc-context";
import { OverviewProvider } from "@/context/overviewContext";
import { StateTableProvider } from "@/context/stateFilterContext";
import { TrendProvider } from "@/context/trendContext";
import { SnackbarProvider } from "@/hooks/Snackbar";
import theme from "@/theme";

import "./index.css";

import { I18nextProvider } from "react-i18next";
import i18n from "@/i18n";
import { OIDC_METADATA_URL } from "./constant";
import { UserUISettingsProvider } from "./context/userUISettingsContext";
import { oidcConfig } from "./oidcClient";

const BaseLineDevTools = ({ children }) => (
  <>
    <CssBaseline />
    {children}
  </>
);

type ProviderItem<P = PropsWithChildren<any>> = {
  Provider: React.ComponentType<P>;
  props?: P;
};

const Providers = ({ children }: { children: ReactNode }) => {
  const providers: ProviderItem[] = [
    { Provider: StyledEngineProvider, props: { enableCssLayer: true } },
    { Provider: ThemeProvider, props: { theme } },
    { Provider: I18nextProvider, props: { i18n: i18n } }, // This will be overridden by the i18n instance
    { Provider: AuthProvider, props: { ...oidcConfig } },
    { Provider: QueryClientProvider, props: { client: queryClient } },
    { Provider: BaseLineDevTools },
    { Provider: SnackbarProvider },
    { Provider: OverviewProvider },
    { Provider: UserUISettingsProvider },
    { Provider: TrendProvider },
    { Provider: StateTableProvider },
  ];

  broadcastQueryClient({ queryClient, broadcastChannel: "WAI" });
  return providers.reduceRight((acc, { Provider, props }) => {
    return (
      <Provider {...props} key={Provider.displayName}>
        {acc}
      </Provider>
    );
  }, children);
};

// Render the app
const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn("Uncaught error", error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
  });
  root.render(
    <StrictMode>
      <Providers>
        <RouterProvider router={router} />
      </Providers>
    </StrictMode>,
  );
}
